import { getModel } from '../../model/registry'
import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useQueryClient } from '@tanstack/react-query'
import { useStore, useMessage } from '../pageView/Page'
import useApi from '../../hooks/useApi'
import applyOptimistic from '../../../../common/model/utilities/applyOptimistic.js'
import resolveParams from '../../../../common/model/utilities/resolveParams.js'
import optimisticLogicData from '../../../../common/model/logic/optimisticLogicData.js'
import { groupItems, indexBy, tagFor } from '../../../../common/model/utilities/groupItems.js'
import ProcessTabs from './ProcessTabs'
import ProcessHeader from './ProcessHeader'
import ProcessItemList from './ProcessItemList'
import ProcessItemDetail from './ProcessItemDetail'
import ProcessCompletion from './ProcessCompletion'
import MessageBar from '../toast/MessageBar'

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    max-width: ${({ $width }) => $width || '600px'};
    margin: 0 auto;
    padding-bottom: 2rem;
`

const findSection = (sections, type) => sections.find(s => s.type === type)

const registry = {
    header:     ProcessHeader,
    itemDetail: ProcessItemDetail,
    completion: ProcessCompletion,
}

const ProcessView = ({ config: pageConfig }) => {
    // The view key roots every test id in this screen, so a spec addresses
    // `warehousePick.toPick.1.pick` the same way it addresses a card's fields.
    const viewKey = pageConfig.view ?? pageConfig.config
    const viewConfig = getModel('processViewData')[viewKey]
    const { sections, entity, width, items, progress } = viewConfig
    const linesField    = items.array
    const sortOrderField = items.sortField
    const { id } = useParams()
    const [state, setState] = useStore(s => s)
    const { postMe } = useApi()
    const [message, setMessage] = useMessage((s) => s)
    const queryClient = useQueryClient()

    // ── section resolution ─────────────────────────────────────────────────
    const headerSection     = findSection(sections, 'header')
    const selectorSection   = findSection(sections, 'selector')
    const itemDetailSection = findSection(sections, 'itemDetail')

    // ── local ui state (derived from sections that declare uiDefault) ───────
    const [activeSelector, setActiveSelector] = useState(selectorSection?.default ?? selectorSection?.options[0]?.id)
    const [ui, setUi] = useState(() =>
        Object.fromEntries(sections.filter(s => 'uiDefault' in s).map(s => [s.type, s.uiDefault]))
    )
    const setUiKey = (key, value) => setUi(prev => ({ ...prev, [key]: value }))

    const itemCardSection = sections.find(s => s.type === 'itemList' && s.selector === activeSelector)
    const doneField       = items.doneField ?? 'done'
    const completedField  = headerSection?.completedField
    const isCompleted     = completedField ? !!state[completedField] : false

    // ── normalise lines ────────────────────────────────────────────────────
    const lines = useMemo(() =>
        (state[linesField] || []).map((line, i) => ({ ...line, id: line.id || `line-${i}` })),
        [state[linesField]]
    )

    // ── item groups ────────────────────────────────────────────────────────
    // A process may batch its items — every one bound for the same destination,
    // every operation at the same station. How to group them, how to order the
    // groups and what to call them is declared per view; see `groups` in
    // common/model/utilities/groupItems.js.
    const groupIndex = useMemo(
        () => indexBy(state, viewConfig.groups?.tag),
        [state[viewConfig.groups?.tag?.from ?? '']])

    const tagForItem = (item) => tagFor(item, viewConfig.groups?.tag, groupIndex)

    const groups = useMemo(
        () => groupItems(lines, viewConfig.groups, state),
        [lines, state[viewConfig.groups?.sequence?.from ?? '']])

    // ── cache invalidation ─────────────────────────────────────────────────
    // An invalidation pattern names its own parameters — `product/:itemId/…`
    // wants an itemId. Read them out of the pattern rather than assuming which
    // fields an item has.
    const contextsForItems = (patterns) => {
        const keys = [...new Set((patterns ?? [])
            .flatMap(p => [...p.matchAll(/:(\w+)/g)].map(m => m[1])))]
            .filter(k => k !== 'id')
        if (!keys.length) return [{}]

        const seen = new Set()
        const out = []
        for (const item of lines) {
            const ctx = Object.fromEntries(keys.map(k => [k, item[k]]))
            if (Object.values(ctx).every(v => v == null)) continue
            const signature = JSON.stringify(ctx)
            if (seen.has(signature)) continue
            seen.add(signature)
            out.push(ctx)
        }
        return out.length ? out : [{}]
    }

    const invalidateFromConfig = (patterns, params = {}) => {
        if (!patterns?.length) return
        const ctx = { id, ...params }
        for (const pattern of patterns) {
            const resolved = pattern.replace(/:(\w+)/g, (_, key) => ctx[key] ?? key)
            queryClient.invalidateQueries({ queryKey: ['list', resolved] })
        }
    }

    // ── line modify ────────────────────────────────────────────────────────
    const callModify = async (operation, endpoint, body, effect = {}) => {
        // The optimistic patch is what the worker sees before the round trip
        // lands. Keep what it replaced: if the call fails the patch is a lie,
        // and a warehouse worker reading a picked line that the server never
        // recorded is worse than a slow one.
        const before = state
        setState(applyOptimistic(entity, operation, state, body))

        let result
        try {
            // postMe returns err.response.data on failure, and throws outright
            // when there is no response at all — a dropped signal, which is the
            // normal case on a warehouse floor.
            result = await postMe(`${entity}/${id}/${endpoint}`, { operation, ...body })
        } catch {
            result = { error: true }
        }

        // A rejected operation comes back as the server's error envelope
        // ({ status, title, message }), not as an `error` flag — check both.
        if (!result || result.error || result.status === 'error') {
            setState(before)
            setMessage({
                show: true, type: 'error',
                title: result?.title, message: result?.message ?? 'error.network',
            })
            return
        }

        if (result.data) setState(result.data)
        invalidateFromConfig(effect.invalidate, body)
    }

    // ── operations ─────────────────────────────────────────────────────────
    // What an operation posts, and where, is declared per entity in
    // optimisticLogicData.requests. Nothing here names a warehouse verb: the
    // engine looks the operation up, resolves its body, and sends it.
    const requests = optimisticLogicData[entity]?.requests ?? {}

    // What the acting section contributes to the body. A card acts on the item
    // as it stands, so its input is read straight off the item; the item detail
    // acts on what the worker entered there instead. Which fields those are is
    // declared as `items.input`, so the engine does not have to go looking for a
    // block by the name of the plugin that renders it.
    const inputFor = (item) => Object.fromEntries(
        Object.entries(items.input ?? {}).map(([key, field]) => [key, item?.[field] ?? null]))

    const findItem = (itemId) =>
        lines.find(l => l.id === itemId) ?? groups.find(g => g.id === itemId)

    const runItemOperation = async (operation, item, effect = {}, input) => {
        const request = requests[operation]
        if (!request || !item) return

        // A composition: run an inner operation once per matching item.
        if (request.forEach) {
            const where = resolveParams(request.forEach.where, { item, record: state })
            const targets = lines.filter(l =>
                Object.entries(where).every(([field, value]) => l[field] === value))
            for (const target of targets) {
                await runItemOperation(request.forEach.operation, target, effect)
            }
            return
        }

        const body = resolveParams(
            request.body,
            { item, record: state, input: input ?? inputFor(item) },
            () => crypto.randomUUID(),
        )
        await callModify(operation, request.endpoint, body, effect)
    }

    // Whole-document operations: the body is the record rather than a declared one, and
    // there is no optimistic patch to roll back — but the failure still has to
    // be shown. A rejected `complete` used to close the modal and reload, which
    // is indistinguishable from success until the worker notices the order is
    // still open.
    const runDocumentOperation = async (operation, effect = {}) => {
        let result
        try {
            result = await postMe(`${entity}/${id}/${requests[operation]?.endpoint ?? operation}`, state)
        } catch {
            result = { error: true }
        }

        if (!result || result.error || result.status === 'error') {
            setMessage({
                show: true, type: 'error',
                title: result?.title, message: result?.message ?? 'error.network',
            })
            return
        }

        setUiKey('completion', false)
        if (effect?.reload) setState({ ...state, loaded: false })
        // A document operation touches every item, so each distinct set of
        // pattern parameters has to be invalidated once.
        contextsForItems(effect?.invalidate).forEach(ctx =>
            invalidateFromConfig(effect?.invalidate, ctx))
    }

    // The item detail hands over the quantity and location the worker chose. An
    // effect that declares a `splitOperation` switches to it when they took less
    // than the item asked for — the remainder becomes a new item.
    const runFromDetail = async (effect, itemId, qty, location) => {
        const item = findItem(itemId)
        if (!item) return

        const bodyColumns = itemDetailSection?.blocks?.find(b => b.type === 'body')?.columns ?? []
        const qtyElement  = bodyColumns.flatMap(c => c.elements || [])
            .find(f => f.type === 'quantitySplit' || f.type === 'quantityInput')
        const plannedQty  = item[qtyElement?.planned ?? qtyElement?.value ?? 'quantity']

        const splitting = effect.splitOperation && qty < plannedQty
        const operation = splitting ? effect.splitOperation : effect.operation

        await runItemOperation(operation, item, effect,
            { qty, location, remainingQty: plannedQty - qty })
        setUiKey('itemDetail', null)
    }

    const handleOpenDetail = (lineId) => {
        const line = lines.find(l => l.id === lineId)
        if (line) {
            const matchingOption = selectorSection?.options.find(
                o => o.filter && line[o.filter.field] === o.filter.value
            )
            if (matchingOption) setActiveSelector(matchingOption.id)
        }
        setUiKey('itemDetail', lineId)
    }

    // ── action dispatch ────────────────────────────────────────────────────
    // Effects carry extra positional arguments from whichever section raised
    // them: an item id, and from the item detail the quantity and location the
    // worker entered.
    const runEffects = (effects, itemId, qty, location) => {
        for (const effect of (effects || [])) {
            if (effect.effect === 'setUi') {
                setUiKey(effect.target, effect.value)
                continue
            }
            if (effect.effect !== 'runOperation') continue

            if (requests[effect.operation]?.document) {
                runDocumentOperation(effect.operation, effect)
            } else if (qty !== undefined) {
                runFromDetail(effect, itemId, qty, location)
            } else {
                runItemOperation(effect.operation, findItem(itemId), effect)
            }
        }
    }

    // ── common props passed to all section components ──────────────────────
    const commonProps = {
        doc:          state,
        lines,
        ui,
        onAction:     runEffects,
        onOpenDetail: handleOpenDetail,
        doneField,
        linesField,
        progress,
        viewKey,
        itemInput: items.input,
        backOverride: pageConfig.backOverride,
    }

    // ── build tabs ─────────────────────────────────────────────────────────
    const detailLine      = ui.itemDetail ? lines.find(l => l.id === ui.itemDetail) : null
    const DetailComponent = registry[itemDetailSection?.type]

    const tabsConfig = (selectorSection?.options ?? []).map(option => {
        const card    = sections.find(s => s.type === 'itemList' && s.selector === option.id)

        // Drop-stop tabs render the consolidated route drop stops (one card per
        // destination) rather than individual lines.
        if (option.source === 'groups') {
            const shown = option.groupFilter
                ? groups.filter(g => g[option.groupFilter.field] === option.groupFilter.value)
                : groups
            return {
                key:    option.id,
                label:  option.label,
                testId: `${viewKey}.tab.${option.id}`,
                content: <ProcessItemList lines={shown} allLines={lines} linesField={linesField} section={card} onAction={runEffects} onOpenDetail={handleOpenDetail} completed={isCompleted} testIdBase={card && `${viewKey}.${card.id}`} />,
            }
        }

        // A tab filters by a single `filter` {field,value} or a compound `filters`
        // array (all must match, e.g. done && !dropped), and sorts by its own
        // `sortField` (default sortOrder — drop tabs use dropSortOrder).
        const hasFilter = !!(option.filter || option.filters)
        // A condition matches by equality (`value`) or inequality (`notValue`).
        // notValue treats an unset field as "not equal" — e.g. a freshly picked
        // line with dropped=undefined still matches { field:'dropped', notValue:true }.
        const matchCond = (l, f) => ('notValue' in f) ? l[f.field] !== f.notValue : l[f.field] === f.value
        const matchLine = (l) =>
            option.filters ? option.filters.every(f => matchCond(l, f))
            : option.filter ? l[option.filter.field] === option.filter.value
            : true
        const sortField = option.sortField ?? sortOrderField
        const filtered = (hasFilter
            ? [...lines].filter(matchLine).sort((a, b) => (a[sortField] ?? 0) - (b[sortField] ?? 0))
            : lines
        ).map(l => l._tag ? l : { ...l, _tag: tagForItem(l) })

        const isDetailForThisTab = detailLine && option.filter
            ? detailLine[option.filter.field] === option.filter.value
            : false

        return {
            key:    option.id,
            label:  option.label,
            testId: `${viewKey}.tab.${option.id}`,
            content: isDetailForThisTab && DetailComponent
                ? <DetailComponent key="detail" section={itemDetailSection} {...commonProps} />
                : <ProcessItemList lines={filtered} allLines={lines} linesField={linesField} section={card} onAction={runEffects} onOpenDetail={handleOpenDetail} completed={isCompleted} testIdBase={card && `${viewKey}.${card.id}`} />,
        }
    })

    // ── render ─────────────────────────────────────────────────────────────
    const overlaySections = sections.filter(s => 'uiDefault' in s && s.type !== 'itemDetail')

    return (
        <Wrapper $width={width}>
            {message.show && (
                <MessageBar title={message.title} message={message.message}
                    type={message.type} setMessage={setMessage} />
            )}

            {headerSection && (() => {
                const C = registry[headerSection.type]
                return C && <C section={headerSection} {...commonProps} />
            })()}

            <ProcessTabs
                tabs={tabsConfig}
                activeTab={activeSelector}
                onTabChange={(key) => { setActiveSelector(key); setUiKey('itemDetail', null) }}
            />

            {overlaySections.map(s => {
                if (!ui[s.type]) return null
                const C = registry[s.type]
                return C ? <C key={s.type} section={s} {...commonProps} /> : null
            })}
        </Wrapper>
    )
}

export default ProcessView
